import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Warning, Plugs } from "@phosphor-icons/react";

export default function DisconnectClientModal({ open, org, onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setConfirm(""); setBusy(false); } }, [open, org?.tenant_id]);

  const name = org?.tenant_name || "";
  const matches = confirm.trim().toLowerCase() === name.trim().toLowerCase() && name.length > 0;

  const disconnect = async () => {
    if (!matches) return;
    setBusy(true);
    try {
      const { data } = await api.post("/xero/organisations/disconnect", {
        tenant_id: org.tenant_id, confirm_name: confirm.trim(),
      });
      const removed = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`${name} disconnected — removed the Xero link and ${removed} record(s).`);
      onDone?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not disconnect the client");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl border-slate-200" data-testid="disconnect-client-modal">
        <DialogHeader>
          <div className="w-11 h-11 rounded-xl bg-negative/10 flex items-center justify-center mb-2">
            <Warning size={22} className="text-negative" weight="fill" />
          </div>
          <DialogTitle className="font-heading font-bold text-2xl tracking-tight text-navy">
            Disconnect {name || "client"}
          </DialogTitle>
          <DialogDescription className="text-navy/70">
            This permanently removes the Xero connection for this client <b>and all of its data</b> — release
            schedules, journals, registers, reconciliations and entity settings. It also frees a slot in your
            plan. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="block text-sm text-navy/70">
            Type <span className="font-semibold text-navy">{name}</span> to confirm
          </label>
          <input autoFocus value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid="disconnect-confirm-input" placeholder={name}
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-navy outline-none focus:border-negative focus:ring-2 focus:ring-negative/20" />

          <div className="flex justify-end gap-3 pt-1">
            <button onClick={onClose} disabled={busy} data-testid="disconnect-cancel"
              className="px-5 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors disabled:opacity-60">
              Cancel
            </button>
            <button onClick={disconnect} disabled={!matches || busy} data-testid="disconnect-confirm-btn"
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-lg bg-negative text-white hover:bg-negative/90 transition-colors disabled:opacity-40">
              <Plugs size={16} /> {busy ? "DISCONNECTING…" : "Disconnect client"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
